<script setup>
import { ref } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  property:  { type: Object, required: true },
  base_path: { type: String, default: '/property' }
})

const activeIdx = ref(0)
const photos = props.property.photo_urls?.length ? props.property.photo_urls : []
</script>

<template>
  <Head>
    <title>{{ property.title }} · {{ t('public.head_title') }}</title>
    <meta
      name="description"
      :content="property.summary || property.description"
    >
  </Head>

  <section class="show">
    <a
      :href="base_path"
      class="back"
    >← {{ t('public.properties.back') }}</a>
    <h1 class="show__title">
      {{ property.title }}
    </h1>
    <p
      v-if="property.summary"
      class="show__summary"
    >
      {{ property.summary }}
    </p>

    <div class="show__layout">
      <div class="gallery">
        <div class="gallery__main">
          <img
            v-if="photos[activeIdx]"
            :src="photos[activeIdx]"
            :alt="property.title"
          >
          <CatalogPlaceholder
            v-else
            kind="property"
            :category="property.category"
          />
        </div>
        <ul
          v-if="photos.length > 1"
          class="thumbs"
        >
          <li
            v-for="(p, i) in photos"
            :key="p"
          >
            <button
              :class="['thumb', i === activeIdx && 'is-active']"
              @click="activeIdx = i"
            >
              <img
                :src="p"
                alt=""
              >
            </button>
          </li>
        </ul>
      </div>

      <div class="info">
        <section
          v-if="property.description"
          class="card"
        >
          <h2 class="section-title">
            {{ t('public.properties.sections.about') }}
          </h2>
          <p class="prose">
            {{ property.description }}
          </p>
        </section>

        <section class="card">
          <h2 class="section-title">
            {{ t('public.properties.sections.details') }}
          </h2>
          <dl class="dl">
            <div v-if="property.area">
              <dt>{{ t('public.properties.fields.area') }}</dt><dd>{{ property.area }}</dd>
            </div>
            <div v-if="property.bedrooms">
              <dt>{{ t('public.properties.fields.bedrooms') }}</dt><dd>{{ property.bedrooms }}</dd>
            </div>
            <div v-if="property.bathrooms">
              <dt>{{ t('public.properties.fields.bathrooms') }}</dt><dd>{{ property.bathrooms }}</dd>
            </div>
            <div v-if="property.max_guests">
              <dt>{{ t('public.properties.fields.max_guests') }}</dt><dd>{{ property.max_guests }}</dd>
            </div>
            <div v-if="property.size_sqm">
              <dt>{{ t('public.properties.fields.size_sqm') }}</dt><dd>{{ property.size_sqm }}</dd>
            </div>
            <div v-if="property.price_per_night">
              <dt>{{ t('public.properties.fields.price_per_night') }}</dt><dd class="dl__price">{{ property.price_per_night }}</dd>
            </div>
            <div v-if="property.price_per_month">
              <dt>{{ t('public.properties.fields.price_per_month') }}</dt><dd class="dl__price">{{ property.price_per_month }}</dd>
            </div>
          </dl>
        </section>

        <section
          v-if="property.amenities && property.amenities.length"
          class="card"
        >
          <h2 class="section-title">
            {{ t('public.properties.sections.amenities') }}
          </h2>
          <ul class="amenities">
            <li
              v-for="a in property.amenities"
              :key="a"
              class="amenity"
            >
              {{ t(`host_portal.property.amenities.${a}`) }}
            </li>
          </ul>
        </section>

        <section
          v-if="property.blackouts && property.blackouts.length"
          class="card"
        >
          <h2 class="section-title">
            {{ t('public.properties.sections.unavailable') }}
          </h2>
          <ul class="blackouts">
            <li
              v-for="(b, i) in property.blackouts"
              :key="i"
              class="blackout"
            >
              {{ b.start_on }} → {{ b.end_on }}
            </li>
          </ul>
        </section>

        <section
          v-if="property.contact_intents && Object.keys(property.contact_intents).length"
          class="card cta"
        >
          <h2 class="section-title">
            {{ t('public.properties.cta_title') }}
          </h2>
          <div class="cta__buttons">
            <a
              v-for="(url, platform) in property.contact_intents"
              :key="platform"
              :href="url"
              target="_blank"
              rel="noopener"
              class="cta__btn"
            >{{ t('public.write_on', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }) }}</a>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>

<style scoped>
.show { max-width: 72rem; margin: 0 auto; padding: clamp(2rem, 5vw, 3rem) 1.5rem 4rem; }
.back { color: var(--muted); text-decoration: none; font-size: 0.875rem; }
.show__title { font-family: var(--font-display, var(--display)); font-size: clamp(2rem, 4.5vw, 2.75rem); letter-spacing: -0.02em; margin: 0.5rem 0 0.5rem; color: var(--ink); font-weight: 800; }
.show__summary { color: var(--inkSoft); margin: 0 0 1.5rem; font-size: 1.0625rem; line-height: 1.6; }
.show__layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
@media (min-width: 880px) { .show__layout { grid-template-columns: 1.1fr 1fr; } }
.gallery__main { aspect-ratio: 4 / 3; background: var(--surfaceAlt); border-radius: 0.75rem; overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.gallery__placeholder { font-size: 4rem; font-weight: 700; color: var(--muted); }
.thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 4.5rem; height: 3.5rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.info { display: grid; gap: 1rem; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 0.75rem; padding: 1.25rem; }
.section-title { font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.dl { display: grid; gap: 0.5rem; margin: 0; }
.dl > div { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem; align-items: baseline; }
.dl dt { color: var(--muted); font-size: 0.8125rem; }
.dl dd { margin: 0; color: var(--ink); font-size: 0.9375rem; }
.dl__price { color: var(--accent); font-weight: 600; }
.amenities { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 0.4rem; }
.amenity { padding: 0.25rem 0.7rem; border: 1px solid var(--line); border-radius: 999px; font-size: 0.8125rem; color: var(--inkSoft); }
.blackouts { list-style: none; padding: 0; margin: 0; display: grid; gap: 0.3rem; }
.blackout { font-size: 0.875rem; color: var(--inkSoft); }
.cta__buttons { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.cta__btn { padding: 0.5rem 0.95rem; background: var(--accent); color: var(--accentInk); border-radius: var(--radius-button, 8px); text-decoration: none; font-size: 0.875rem; font-weight: 600; }
</style>
